import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import './AttributeManager.css';

const AttributeManager = () => {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'select',
    values: [],
    isRequired: false,
    displayOrder: 0
  });
  const [newValue, setNewValue] = useState('');

  useEffect(() => {
    fetchAttributes();
  }, []);

  const fetchAttributes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/product-attributes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttributes(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch attributes');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAddValue = () => {
    if (!newValue.trim()) {
      toast.error('Please enter a value');
      return;
    }

    setFormData({
      ...formData,
      values: [...formData.values, { value: newValue.trim(), priceModifier: 0, stock: 0, isActive: true }]
    });
    setNewValue('');
  };

  const handleRemoveValue = (index) => {
    setFormData({
      ...formData,
      values: formData.values.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    // Prevent any default form behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }
      
      const data = {
        ...formData,
        values: formData.values.map(v => ({
          value: v.value || v,
          priceModifier: v.priceModifier || 0,
          stock: v.stock || 0,
          isActive: v.isActive !== false
        }))
      };

      if (editingAttribute) {
        await axios.put(`${API_URL}/api/product-attributes/${editingAttribute._id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Attribute updated successfully');
      } else {
        await axios.post(`${API_URL}/api/product-attributes`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Attribute created successfully');
      }

      setShowModal(false);
      setEditingAttribute(null);
      setFormData({
        name: '',
        type: 'select',
        values: [],
        isRequired: false,
        displayOrder: 0
      });
      fetchAttributes();
    } catch (error) {
      console.error('Attribute submit error:', error);
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else {
        toast.error(error.response?.data?.message || 'Operation failed');
      }
      // Don't close modal on error - keep form data so user can fix and retry
      return false;
    }
  };

  const handleEdit = (attribute) => {
    setEditingAttribute(attribute);
    setFormData({
      name: attribute.name,
      type: attribute.type || 'select',
      values: attribute.values || [],
      isRequired: attribute.isRequired || false,
      displayOrder: attribute.displayOrder || 0
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this attribute?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/product-attributes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Attribute deleted successfully');
      fetchAttributes();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Delete failed');
    }
  };

  const openModal = () => {
    setEditingAttribute(null);
    setFormData({
      name: '',
      type: 'select',
      values: [],
      isRequired: false,
      displayOrder: 0
    });
    setShowModal(true);
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>Attribute Manager</h1>
        <button onClick={openModal} className="btn btn-primary">
          + Add Attribute
        </button>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Values</th>
              <th>Required</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {attributes.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center' }}>
                  No attributes found. Create one to use in product variants.
                </td>
              </tr>
            ) : (
              attributes.map((attr) => (
                <tr key={attr._id}>
                  <td>{attr.name}</td>
                  <td>{attr.type}</td>
                  <td>
                    {attr.values?.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {attr.values.slice(0, 5).map((val, idx) => (
                          <span key={idx} className="badge badge-secondary">
                            {val.value || val}
                          </span>
                        ))}
                        {attr.values.length > 5 && (
                          <span className="badge badge-secondary">+{attr.values.length - 5} more</span>
                        )}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{attr.isRequired ? 'Yes' : 'No'}</td>
                  <td>
                    <button
                      onClick={() => handleEdit(attr)}
                      className="btn btn-sm btn-primary"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(attr._id)}
                      className="btn btn-sm btn-danger"
                      style={{ marginLeft: '5px' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingAttribute ? 'Edit Attribute' : 'Add Attribute'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e);
              }} 
              noValidate
            >
              <div className="form-group">
                <label>Attribute Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Size, Color, Brand"
                  required
                />
              </div>

              <div className="form-group">
                <label>Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                >
                  <option value="select">Select</option>
                  <option value="text">Text</option>
                  <option value="color">Color</option>
                  <option value="size">Size</option>
                  <option value="number">Number</option>
                </select>
              </div>

              <div className="form-group">
                <label>Values *</label>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <input
                    type="text"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder="Enter value (e.g., Small, Red)"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddValue())}
                  />
                  <button type="button" onClick={handleAddValue} className="btn btn-secondary">
                    Add
                  </button>
                </div>
                {formData.values.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '10px' }}>
                    {formData.values.map((val, index) => (
                      <span key={index} className="badge badge-primary" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {val.value || val}
                        <button
                          type="button"
                          onClick={() => handleRemoveValue(index)}
                          style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '16px' }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    name="isRequired"
                    checked={formData.isRequired}
                    onChange={handleInputChange}
                  />
                  <span>Required Attribute</span>
                </label>
              </div>

              <div className="form-group">
                <label>Display Order</label>
                <input
                  type="number"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                  min="0"
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingAttribute ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttributeManager;
